import {
  action, computed, makeAutoObservable, observable, runInAction, when,
} from 'mobx';
import { ThemeImageKeys, ThemeSvgKeys, VisibilityOpt } from '@gamiphy/bl/theme';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { isMobile } from '@gamiphy/utils/device';
import { ThemeDto, ThemesApi } from '@coretava/api/core-service';

class ThemeStore {
  @observable initialized = false;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  @observable theme!: ThemeDto = undefined;

  constructor() {
    makeAutoObservable(this);

    void this.initialize();
  }

  @computed
  get api(): ThemesApi {
    return new ThemesApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
    });
  }

  @computed get primaryColor(): string {
    return this.theme && this.theme.palette && this.theme.palette.primary ? this.theme.palette.primary.main || '' : '';
  }

  @computed get cardBorderRadius(): number {
    return (this.theme && this.theme.card) ? this.theme.card.borderRadius || 0 : 0;
  }

  @computed get mainBorderRadius(): number | undefined {
    return (this.theme && this.theme.main) ? this.theme.main.borderRadius : 0;
  }

  @computed get circleCardBackground(): string | undefined {
    return this.theme.card?.circle?.background ?? '';
  }

  @computed get congratsIcon(): string {
    return this.theme && this.theme.onboarding && this.theme.onboarding.congrats ? this.theme.onboarding.congrats.icon || '' : '';
  }

  @computed get introAnimation(): string {
    return this.theme && this.theme.onboarding && this.theme.onboarding.intro ? this.theme.onboarding.intro.animation || '' : '';
  }

  @computed get introBackground(): string {
    return this.theme && this.theme.onboarding && this.theme.onboarding.intro ? this.theme.onboarding.intro.background || '' : '';
  }

  @computed get chestAnimation(): string {
    return this.theme && this.theme.chest ? this.theme.chest.animation || '' : '';
  }

  @computed get getSecondaryColor(): string {
    return (this.theme && this.theme.palette && this.theme.palette.text) ? this.theme.palette.text.secondary || '' : '';
  }

  @computed get welcomeCoupon(): string {
    return this.theme && this.theme.onboarding && this.theme.onboarding.welcome ? this.theme.onboarding.welcome.icon || '' : '';
  }

  @computed get levelUp(): string {
    return this.theme?.levelUp?.background || '';
  }

  @computed get branding(): boolean {
    return this.theme?.branding || false;
  }

  @computed get iconSize(): number {
    return this.theme.launcher.size ?? 60;
  }

  @computed get cashbackPopupsHidden(): boolean {
    return isMobile() ? !!this.theme.hidePopups?.mobile : !!this.theme.hidePopups?.desktop;
  }

  @computed get isVisible(): boolean {
    if (window.location.pathname.includes('/account') || window.location.pathname.includes('/login')) {
      return false;
    }
    if (window.location.pathname.includes('/checkout')) {
      return false;
    }
    if (this.theme.visibility) {
      try {
        const currentURL = `${window.location.origin}${window.location.pathname}`;
        const urlObj = new URL(this.theme.visibility.url);
        const url = `${urlObj.origin}${urlObj.pathname}`;
        const operation = this.theme.visibility.opt;
        switch (operation) {
          case VisibilityOpt.IS:
            if (currentURL !== url) {
              return false;
            }
            break;
          case VisibilityOpt.IS_NOT:
            if (currentURL === url) {
              return false;
            }
            break;
          default:
          // do nothing
        }
      } catch {
        // in case of invalid URL we show the widget on all pages
      }
    }
    return !(isMobile() ? this.theme?.widget?.hidden?.mobile : this.theme?.widget?.hidden?.desktop);
  }

  @action
  async initialize(): Promise<void> {
    await when(() => AppStore.initialized);

    const { data: { theme } } = await this.api.themesControllerGetByApp(AppStore.app.id);

    if (theme) {
      runInAction(() => {
        this.theme = theme;
        this.initialized = true;
      });
    }
  }

  /**
   * Get image url based on the theme
   * @param key The image key
   * @param defaultValue Image default url
   */
  getImageUrl(key: ThemeImageKeys, defaultValue: string): string {
    return (this.theme.image as (Record<string, string> | undefined))?.[key] ?? defaultValue;
  }

  /**
   * Get svg url based on the theme
   * @param key The svg key
   * @param defaultValue Svg default url
   */
  getSvgUrl(key: ThemeSvgKeys, defaultValue: string): string {
    return (this.theme.svg as (Record<string, string> | undefined))?.[key] ?? defaultValue;
  }
}

export default new ThemeStore();
